/* global window, fetch, console, FormData */
import * as Sentry from '@sentry/react';
import debounce from 'awesome-debounce-promise';
import camelize from 'camelize';
import isNumber from 'lodash/isNumber';
import QueryString from 'qs';
import snakeize from 'snakeize';
import 'whatwg-fetch';
import { authService } from '../services/authService';
import { phoneVerificationService } from '../services/phoneVerificationService';
import { isAnonymousDiscoverSurface } from '../utils/domain';
import { getCookie, removeCookie } from '../utils/tokens';
import {
    extractMessageFromResponse,
    isPhoneVerificationRequiredError,
    isSessionEndedError,
} from './getApiErrorMessage';

export const apiHost = import.meta.env.VITE_APP_ENV === 'prod' ? '/api' : '/api-dev';
const DEBOUNCE_DELAY = 1000;

const baseUrl = () => {
    return `${apiHost}/`;
};

export const get = (endpoint, requirements = {}) => {
    const { requireToken, showErrorCode } = requirements;

    if (typeof requireToken === 'boolean') {
        return call(endpoint, {}, requireToken, showErrorCode);
    }
    return call(endpoint, {}, true, showErrorCode);
};

export const post = (endpoint, payload, requirements = {}) => {
    const { requireToken = true, showErrorCode } = requirements;

    return call(
        endpoint,
        {
            body: JSON.stringify(snakeize(payload)),
            method: 'POST',
        },
        requireToken,
        showErrorCode
    );
};

export const put = (endpoint, payload, requirements = {}) => {
    const { showErrorCode } = requirements;

    return call(
        endpoint,
        {
            body: JSON.stringify(snakeize(payload)),
            method: 'PUT',
        },
        true,
        showErrorCode
    );
};

export const patch = (endpoint, payload, requirements = {}) => {
    const { showErrorCode } = requirements;

    return call(
        endpoint,
        {
            body: JSON.stringify(snakeize(payload)),
            method: 'PATCH',
        },
        true,
        showErrorCode
    );
};

export const deleteCall = (endpoint, payload, requirements = {}) => {
    const { showErrorCode } = requirements;

    return call(
        endpoint,
        {
            body: JSON.stringify(snakeize(payload)),
            method: 'DELETE',
        },
        true,
        showErrorCode
    );
};

const authRedirect = () => {
    const pathname = window.location.pathname;
    if (!pathname.includes('/sign-in')) {
        const oldPath = encodeURIComponent(pathname + window.location.search);
        window.location = `/sign-in?redirect=${oldPath}`;
        return true;
    }
    return false;
};

const call = async (endpoint, options, requireToken = true, showErrorCode = false) => {
    const pathname = window.location.pathname;
    const isPublicRoute =
        pathname.includes('/login') ||
        pathname.includes('/sign-in') ||
        pathname.includes('/rsvp') ||
        pathname.includes('/accept-team-invite') ||
        pathname.includes('/auth') ||
        isAnonymousDiscoverSurface(pathname) ||
        (pathname.includes('/proposals') && !pathname.includes('/event'));
    const token = getCookie();

    if (requireToken && !token && !isPublicRoute) {
        if (authRedirect()) {
            // Don't resolve after redirect — the page is navigating away
            return new Promise(() => {});
        }
        return;
    } else {
        const path = `${baseUrl()}${endpoint}`;
        const mergedOptions = { ...defaultOptions(token), ...options };

        let responseStatus = { status: '', statusText: '' };

        return fetch(path, mergedOptions)
            .then(res => {
                responseStatus = res;
                return res.json().catch(() => {
                    // Several public endpoints (RSVP, make-cookie) carry credentials in the query
                    // string (email, token, parcel_token) — strip it so a non-JSON 5xx here never
                    // sends those to Sentry as the issue title.
                    const route = endpoint.split('?')[0];
                    return { success: false, message: `Server error (${res.status}) at ${route}` };
                });
            })
            .then(json => camelize(json))
            .then(async camelized => {
                if (!camelized.success) {
                    if (isPhoneVerificationRequiredError(camelized, responseStatus)) {
                        // Open the verification flow at the moment of refusal. Without this the
                        // user is told to fix their profile, finds it already complete, and has no
                        // route to the verification step at all — the profile-setup modal only
                        // opens for users with no phone number saved.
                        phoneVerificationService.triggerPhoneVerificationRequired();
                    }
                    if (isSessionEndedError(camelized, responseStatus)) {
                        // Don't redirect if we're on the /auth route (handling proposal auth with email)
                        const isAuthRoute = pathname.includes('/auth') || isAnonymousDiscoverSurface(pathname);
                        if (!isAuthRoute) {
                            removeCookie();
                            authService.triggerLogout();
                            if (authRedirect()) {
                                // Don't reject after redirect — the page is navigating away
                                return new Promise(() => {});
                            }
                        }
                    }
                    const message = extractMessageFromResponse(camelized, responseStatus);
                    const apiError = Object.assign(new Error(message), {
                        name: 'ApiError',
                        status: responseStatus.status,
                    });
                    if (showErrorCode) {
                        apiError.code = responseStatus.status;
                        apiError.raw = { ...camelized };
                    }
                    return Promise.reject(apiError);
                }
                return camelized;
            })
            .catch(error => {
                console.warn('API request failed at', path, error);
                return Promise.reject(error);
            });
    }
};

const defaultOptions = token => ({
    credentials: 'include',
    headers: {
        Authorization: `Bearer ${token}`,
        'url-for-links':
            import.meta.env.VITE_APP_ENV === 'prod' ? 'https://platform.bizly.com/' : 'https://dev.platform.bizly.com/',
        'content-type': 'application/json',
    },
});

const serializeAgenda = agenda => ({
    id: agenda.id,
    addToInquiry: agenda.addToInquiry,
    attendees: agenda.attendees || 1,
    amenities: {
        av: agenda.avNeeds?.map(avo => avo.id) ?? [],
        foodAndBeverage:
            agenda.fbNeeds?.map(fbo => ({
                optionId: fbo.id,
                styleId: fbo.diningStyle ? fbo.diningStyle.id : null,
            })) ?? [],
    },
    end: agenda.end,
    name: agenda.name,
    roomSetup: agenda.setup && agenda.setup.id,
    start: agenda.start,
    startTime: agenda.startTime,
    endTime: agenda.endTime,
});

const deserializeAgenda = agenda => ({
    attendees: agenda.attendees,
    addToInquiry: agenda.addToInquiry,
    avNeeds: agenda.amenities.av,
    end: agenda.end,
    fbNeeds: agenda.amenities.foodAndBeverage,
    name: agenda.name,
    setupId: agenda.roomSetup,
    start: agenda.start,
    startTime: agenda.startTime,
    endTime: agenda.endTime,
});

export const serializeDay = day => ({
    ...day,
    guestrooms: day.guestrooms || [],
    agenda: day.agenda ? day.agenda.map(a => serializeAgenda(a)) : [],
});
export const deserializeDay = day => ({
    ...day,
    agenda: day.agenda.map(a => deserializeAgenda(a)),
});

const serializeEvent = event => ({
    ...event,
    schedule: event.schedule.map(day => serializeDay(day)),
});

const deserializeEvent = event => {
    return {
        ...event,
        ...(event.schedule ? { schedule: event.schedule.map(day => deserializeDay(day)) } : {}),
    };
};

export const isQuestionValid = question => {
    const { type } = question;

    const questionToValidator = {
        text: ({ value: prompt }) => !!prompt,
        multipleChoice: ({ value: prompt, options = [] }) => !!prompt && options.length >= 2 && options.every(o => o),
        multipleChoiceMultipleAnswers: ({ value: prompt, options = [] }) =>
            !!prompt && options.length >= 2 && options.every(o => o),
        date: ({ value: prompt }) => !!prompt,
        linearScale: ({ value: prompt, options = {} }) =>
            !!prompt && isNumber(options.lowValue) && isNumber(options.highValue),
    };

    const defaultValidator = () => true;

    const validator = questionToValidator[type] || defaultValidator;

    return validator(question);
};

export const updateEvent = debounce(
    event => put(`events/${event.id}`, deserializeEvent(event)).then(json => serializeEvent(json.event)),
    DEBOUNCE_DELAY
);

export const updateEventNoDebounce = event =>
    put(`events/${event.id}`, deserializeEvent(event)).then(json => serializeEvent(json.event));

export const loadEventOptions = eventId => get(`events/${eventId}/options`);

const placesAPIOptions = {
    inputtype: 'textquery',
    fields: ['place_id', 'formatted_address'],
    key: import.meta.env.VITE_APP_GMAPS_KEY,
};

/**
 * Raw request — undebounced. Use this from callers that already debounce
 * upstream (e.g. TanStack Query with a debounced queryKey). The shared
 * `findPlaces` wrapper below is debounced via awesome-debounce-promise at
 * module scope, which means concurrent callers (location autocomplete +
 * name suggestions) compete for the single shared instance and the loser's
 * promise never resolves — see PR #3277 review.
 *
 * @param {string} query
 * @param {number | null | undefined} [lat]
 * @param {number | null | undefined} [lng]
 * @param {boolean} [strictbounds]
 * @param {boolean} [establishment]
 */
export const findPlacesRequest = (query, lat, lng, strictbounds = false, establishment = false) => {
    const options = {
        ...(lat && lng
            ? {
                  location: [lat, lng],
                  radius: 50000,
              }
            : {}),
        input: query,
    };

    if (strictbounds) options.strictbounds = true;
    if (establishment) options.establishment = true;

    return get(`venues/autocomplete?${QueryString.stringify(options, { arrayFormat: 'comma' })}`);
};

export const findPlaces = debounce(findPlacesRequest, 200);

/**
 * Token-free variant — use this on public/unauthenticated pages (e.g. Discover).
 * Mirrors `findPlacesRequest`'s signature so callers (e.g. the "Add from Google"
 * suggestions) get matching behavior, including the `establishment` flag.
 */
export const findPublicPlacesRequest = (query, lat, lng, strictbounds = false, establishment = false) => {
    const options = {
        ...(lat && lng ? { location: [lat, lng], radius: 50000 } : {}),
        input: query,
    };

    if (strictbounds) options.strictbounds = true;
    if (establishment) options.establishment = true;

    return get(`venues/public/autocomplete?${QueryString.stringify(options, { arrayFormat: 'comma' })}`, {
        requireToken: false,
    });
};

export const findCities = debounce(async query => {
    const res = await fetch(
        `/gmaps/maps/api/place/autocomplete/json?${QueryString.stringify(
            { ...placesAPIOptions, types: '(cities)', input: query },
            { arrayFormat: 'comma' }
        )}`
    );
    const json = await res.json();
    return camelize(json);
}, 500);

// Not debounced: this is a one-shot place-details lookup keyed by a discrete
// place_id (fired on selection / mount), never on rapid typing. Debouncing it
// only added a guaranteed ~500ms before the fetch even started.
export const getPlace = query =>
    fetch(`/gmaps/maps/api/place/details/json?place_id=${query}&key=${import.meta.env.VITE_APP_GMAPS_KEY}`)
        .then(res => res.json())
        .then(json => camelize(json));

export const addDocument = (id, document) => post(`events/${id}/resources`, document);

export const removeDocument = (eventId, documentId) => deleteCall(`events/${eventId}/resources/${documentId}`);

export const loadDocuments = id => get(`events/${id}/resources`).then(json => json.resources);

export const loadEventRegistrationInfo = (eventId, slugId) =>
    get(`events/${eventId}/registration-pages/${slugId}`, { requireToken: false });

export const loadPlaybookRegistrationInfo = (playbookId, slugId) =>
    get(`playbooks/${playbookId}/registration-pages/${slugId}`, { requireToken: false });

export const submitEventRegistrationInfo = (eventId, slugId, requestBody) =>
    post(`events/${eventId}/register/${slugId}`, requestBody, { requireToken: false });

export const loadEvent = id => get(`events/${id}`).then(json => serializeEvent(json.event));

export const loadEventWithTemplateAndCollaborators = id =>
    get(`events/${id}?include=template,collaborators`).then(({ event, template, collaborators }) => ({
        event: serializeEvent(event),
        template,
        collaborators,
    }));

export const getPaymentCredentials = paymentCardId => get(`payment-cards/${paymentCardId}/details`);

export const uploadFileToBizly = async (file, directory = 'files') => {
    try {
        const payload = new FormData();
        payload.append('file', file);
        payload.append('directory', directory);

        const token = getCookie();
        const path = `${baseUrl()}/upload`;

        const response = await fetch(path, {
            method: 'POST',
            body: payload,
            credentials: 'include',
            headers: {
                Authorization: `Bearer ${token}`,
                'url-for-links':
                    import.meta.env.VITE_APP_ENV === 'prod'
                        ? 'https://platform.bizly.com/'
                        : 'https://dev.platform.bizly.com/',
            },
        });

        const json = await response.json();

        if (json.error) throw new Error(json.error);
        return json;
    } catch (error) {
        Sentry.captureException(error, {
            tags: { component: 'uploadFileToBizly' },
            extra: { directory },
        });
        throw error;
    }
};

export * from './ai';
export * from './attendees';
export * from './auth';
export * from './bookings';
export * from './collaborators';
export * from './connectors';
export * from './delegatedProposalTokens';
export * from './events';
export { getApiErrorMessage } from './getApiErrorMessage';
export * from './parcels';
export * from './planner';
export * from './proposalForms';
export * from './schedule';
export * from './user';
export * from './venues';
